import React, { useRef, useEffect, useCallback, useState } from 'react';
import { mapCameraToLabel } from '../../utils/CameraAngleMapper';

const DEG = Math.PI / 180;
const CANVAS_SIZE = 280;

function project(cx, cy, focal, x3d, y3d, z3d) {
  const scale = focal / (focal + z3d);
  return { x: cx + x3d * scale, y: cy - y3d * scale, scale };
}

function spherePoint(azimDeg, elevDeg, radius) {
  const a = azimDeg * DEG, e = elevDeg * DEG;
  return {
    x: radius * Math.cos(e) * Math.sin(a),
    y: radius * Math.sin(e),
    z: radius * Math.cos(e) * Math.cos(a),
  };
}

const CameraController = ({ azimuth, elevation, distance, imageUrl, onChange }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const dragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const [localAz, setLocalAz] = useState(azimuth);
  const [localEl, setLocalEl] = useState(elevation);
  const [imgLoaded, setImgLoaded] = useState(false);

  // 预加载参考图到 Image 对象
  useEffect(() => {
    if (!imageUrl) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { imgRef.current = img; setImgLoaded(true); };
    img.src = imageUrl;
  }, [imageUrl]);

  useEffect(() => {
    if (!dragging.current) {
      setLocalAz(azimuth);
      setLocalEl(elevation);
    }
  }, [azimuth, elevation]);

  const draw = useCallback((az, el) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const S = CANVAS_SIZE;
    canvas.width = S * dpr;
    canvas.height = S * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, S, S);

    const cx = S / 2, cy = S / 2;
    const R = S * 0.4;
    const FOCAL = 300;

    // --- 球心绘制参考图（圆形裁剪）---
    const img = imgRef.current;
    if (img) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, R * 0.55, 0, Math.PI * 2);
      ctx.clip();
      // 居中绘制，cover 模式
      const imgR = R * 1.1;
      const imgAspect = img.width / img.height;
      let dw, dh;
      if (imgAspect > 1) {
        dh = imgR * 2;
        dw = dh * imgAspect;
      } else {
        dw = imgR * 2;
        dh = dw / imgAspect;
      }
      ctx.globalAlpha = 0.6;
      ctx.drawImage(img, cx - dw / 2, cy - dh / 2, dw, dh);
      ctx.globalAlpha = 1.0;
      ctx.restore();
    }

    // --- wireframe sphere: latitude ---
    ctx.strokeStyle = 'rgba(255,255,255,0.15)'; // canvas 取景框绘制色，与主题无关，保留
    ctx.lineWidth = 0.5;
    for (let lat = -60; lat <= 60; lat += 30) {
      const r = R * Math.cos(lat * DEG);
      const yOff = R * Math.sin(lat * DEG);
      const p = project(cx, cy, FOCAL, 0, yOff, 0);
      ctx.beginPath();
      ctx.ellipse(cx, p.y, r * p.scale, r * p.scale * 0.3, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    // longitude
    for (let lon = 0; lon < 180; lon += 30) {
      ctx.beginPath();
      for (let t = 0; t <= 360; t += 5) {
        const pt = spherePoint(lon, t, R);
        const p = project(cx, cy, FOCAL, pt.x, pt.y, pt.z);
        t === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
    }

    // --- azimuth ring (cyan) ---
    ctx.strokeStyle = 'rgba(0,200,220,0.5)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let t = 0; t <= 360; t += 3) {
      const pt = spherePoint(t, 0, R);
      const p = project(cx, cy, FOCAL, pt.x, pt.y, pt.z);
      t === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();

    // --- elevation arc (orange) ---
    ctx.strokeStyle = 'rgba(255,160,40,0.6)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let t = -90; t <= 90; t += 3) {
      const pt = spherePoint(az, t, R);
      const p = project(cx, cy, FOCAL, pt.x, pt.y, pt.z);
      t === -90 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();

    // --- dashed line center → camera ---
    const cam = spherePoint(az, el, R);
    const cp = project(cx, cy, FOCAL, cam.x, cam.y, cam.z);
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cp.x, cp.y);
    ctx.strokeStyle = 'rgba(34,197,94,0.35)';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.stroke();
    ctx.setLineDash([]);

    // --- camera dot (green) ---
    const dotR = Math.max(6, 7 * cp.scale);
    ctx.beginPath();
    ctx.arc(cp.x, cp.y, dotR, 0, Math.PI * 2);
    ctx.fillStyle = '#22c55e';
    ctx.fill();
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // camera icon hint (small triangle pointing inward)
    const angle = Math.atan2(cy - cp.y, cx - cp.x);
    ctx.save();
    ctx.translate(cp.x, cp.y);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(dotR + 2, 0);
    ctx.lineTo(dotR + 8, -4);
    ctx.lineTo(dotR + 8, 4);
    ctx.closePath();
    ctx.fillStyle = 'rgba(34,197,94,0.7)';
    ctx.fill();
    ctx.restore();

    // --- angle labels near dot ---
    ctx.font = '10px sans-serif';
    ctx.fillStyle = 'rgba(0,200,220,0.9)';
    ctx.fillText(`${Math.round(az)}°`, cp.x + dotR + 10, cp.y - 1);
    ctx.fillStyle = 'rgba(255,160,40,0.9)';
    ctx.fillText(`${Math.round(el)}°`, cp.x + dotR + 10, cp.y + 10);
  }, [imgLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    draw(localAz, localEl);
  }, [localAz, localEl, draw]);

  // --- Canvas drag: horizontal → azimuth, vertical → elevation ---
  const handlePointerDown = (e) => {
    dragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };
    const newAz = ((localAz + dx * 0.8) % 360 + 360) % 360;
    const newEl = Math.max(-90, Math.min(90, localEl + dy * 0.6));
    setLocalAz(newAz);
    setLocalEl(newEl);
  };

  const handlePointerUp = () => {
    if (dragging.current) {
      dragging.current = false;
      onChange({ azimuth: Math.round(localAz), elevation: Math.round(localEl), distance });
    }
  };

  // Scroll → distance
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.1 : -0.1;
    const newDist = Math.max(0.5, Math.min(3.0, distance + delta));
    onChange({ azimuth: Math.round(localAz), elevation: Math.round(localEl), distance: Math.round(newDist * 10) / 10 });
  };

  const label = mapCameraToLabel(localAz, localEl, distance);

  return (
    <div className="flex flex-col items-center gap-2">
      {/* 球体 Canvas（参考图画在球心） */}
      <canvas
        ref={canvasRef}
        style={{ width: CANVAS_SIZE, height: CANVAS_SIZE, cursor: 'grab', borderRadius: 12 }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onWheel={handleWheel}
      />

      {/* 紧凑滑块行 */}
      <div className="w-full flex gap-3" style={{ maxWidth: CANVAS_SIZE }}>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between text-2xs mb-0.5" style={{ color: 'var(--color-text-muted)' }}>
            <span style={{ color: 'var(--color-accent-primary)' }}>方位</span>
            <span style={{ color: 'var(--color-accent-primary)' }}>{Math.round(localAz)}°</span>
          </div>
          <input
            type="range" min={0} max={360} step={1}
            value={Math.round(localAz)}
            onChange={e => {
              const v = Number(e.target.value);
              setLocalAz(v);
              onChange({ azimuth: v, elevation: Math.round(localEl), distance });
            }}
            className="w-full h-1"
            style={{ accentColor: 'var(--color-accent-primary)' }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between text-2xs mb-0.5" style={{ color: 'var(--color-text-muted)' }}>
            <span style={{ color: 'var(--color-accent-primary)' }}>俯仰</span>
            <span style={{ color: 'var(--color-accent-primary)' }}>{Math.round(localEl)}°</span>
          </div>
          <input
            type="range" min={-90} max={90} step={1}
            value={Math.round(localEl)}
            onChange={e => {
              const v = Number(e.target.value);
              setLocalEl(v);
              onChange({ azimuth: Math.round(localAz), elevation: v, distance });
            }}
            className="w-full h-1"
            style={{ accentColor: 'var(--color-accent-primary)' }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between text-2xs mb-0.5" style={{ color: 'var(--color-text-muted)' }}>
            <span style={{ color: 'var(--color-accent-primary)' }}>距离</span>
            <span style={{ color: 'var(--color-accent-primary)' }}>{distance.toFixed(1)}x</span>
          </div>
          <input
            type="range" min={0.5} max={3.0} step={0.1}
            value={distance}
            onChange={e => onChange({ azimuth: Math.round(localAz), elevation: Math.round(localEl), distance: Number(e.target.value) })}
            className="w-full h-1"
            style={{ accentColor: 'var(--color-accent-primary)' }}
          />
        </div>
      </div>

      {/* 标签 */}
      <div className="text-xs py-0.5 px-3 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>
        {label.fullText}
      </div>
    </div>
  );
};

export default CameraController;
